import { useState } from "react";
import { LightningCharge, Speedometer2, Clock, BarChart } from "react-bootstrap-icons";

import { AdditionalMeterDetail, MeterDetailsViewProps } from "../../types/components.types";
import { downloadCSV } from "../../utils/electralink/electralink.csv";
import Button from "../layout/Button";

// Declare a functional component using <MeterDetailsViewProps> for type safety.
// Pass meterDetails as a prop. meterDetails contains all the relevant details for a specific meter to display the details on the UI and export it as CSV.
const MeterDetailsView: React.FC<MeterDetailsViewProps> = ({ meterDetails }) => {
  const [showMeterDetails, setShowMeterDetails] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);

  const displayMeterDetails = () => {
    setShowMeterDetails(!showMeterDetails);
  };

  // Organise meter details into categories for better display
  const organiseDataForDisplay = () => {
    const completeData = meterDetails;

    // Extract the nested objects and properties for an organised display.
    const { additional_detail, tpr_data, hh_aq_calc, is_half_hourly, ...mainData } = completeData;

    return {
      mainDetails: mainData,
      additionalDetails: additional_detail[0] as AdditionalMeterDetail | undefined,
      tprData: tpr_data,
      aqCalc: hh_aq_calc,
      isHalfHourly: is_half_hourly,
    };
  };

  const handleDownloadCSV = async () => {
    setIsDownloading(true);

    try {
      // Call the "downloadCSV" function from electralink.service.ts.
      const result = await downloadCSV(meterDetails);

      // If the download was not successful, display an error message as an alert on the UI.
      if (!result.success) {
        console.log("Failed to download CSV:", result.error);
        alert(`Failed to download CSV: ${result.error ?? "Unknown error"}. Please try again.`);
      }
    } catch (error) {
      // If an unexpected error occurs during the download process, log it for debugging purposes.
      console.log("Error during CSV download:", error);
      alert("An error occurred while downloading the CSV. Please try again.");
    } finally {
      // Set `isDownloading` back to false so the button is re-enabled on the UI.
      setIsDownloading(false);
    }
  };

  const { mainDetails, additionalDetails, tprData, aqCalc, isHalfHourly } =
    organiseDataForDisplay();

  return (
    <div className="meter-details" data-testid="electralink-meter-details">
      {showMeterDetails ? (
        <>
          <div className="meter-details-header">
            <h2>
              <LightningCharge className="me-2" />
              Meter Details
            </h2>
            {/* Render the Button component to trigger the CSV download when clicked. */}
            {/* The button is disabled while `isDownloading` is true. This is to prevent multiple user clicks. */}
            <Button
              onClick={handleDownloadCSV}
              disabled={isDownloading}
              testId="electralink-meter-details-download-csv"
            >
              {/* If the meter is half-hourly, display 'Download Half-Hourly CSV'. Otherwise, display 'Download CSV'. */}
              {meterDetails.is_half_hourly ? "Download Half-Hourly CSV" : "Download CSV"}
            </Button>
          </div>

          <div className="row g-3">
            <table
              className="table table-bordered"
              style={{ width: "90%", margin: "0 auto", marginTop: "15px" }}
            >
              <tbody>
                {Object.entries(mainDetails)
                  // Filter out empty/null/undefined values to not render on the UI.
                  .filter(([key, value]) => key !== "status_code" && value !== "")
                  .map(([key, value]) => (
                    <tr key={key}>
                      <th style={{ width: "20%" }}>{key.replace(/_/g, " ").toUpperCase()}</th>
                      <td>{String(value)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {/* Display the additional details section */}
          {additionalDetails && Object.keys(additionalDetails).length > 0 && (
            <div className="mt-4">
              <div className="meter-details-header">
                <h2>
                  <Speedometer2 className="me-2" />
                  Additional Meter Details
                </h2>
              </div>
              <div className="row g-3">
                <table
                  className="table table-bordered"
                  style={{ width: "90%", margin: "0 auto", marginTop: "15px" }}
                >
                  <tbody>
                    {Object.entries(additionalDetails)
                      .filter(
                        ([_key, value]) => value !== "" && value !== null && value !== undefined,
                      )
                      .map(([key, value]) => (
                        <tr key={key}>
                          <th style={{ width: "20%" }}>{key.replace(/_/g, " ").toUpperCase()}</th>
                          <td>{String(value)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Display the TPR data */}
          {tprData.length > 0 && (
            <div className="mt-4">
              <div className="meter-details-header">
                <h2>
                  <Clock className="me-2" />
                  TPR Data
                </h2>
              </div>
              {tprData.map((tpr, index) => (
                <div key={index} className="tpr-data-row mb-3">
                  {index > 0 && <div className="tpr-separator" />}
                  <div className="row g-3">
                    <table
                      className="table table-bordered"
                      style={{ width: "90%", margin: "0 auto", marginTop: "15px" }}
                    >
                      <tbody>
                        <tr>
                          <th style={{ width: "20%" }}>TPR EAC</th>
                          <td>{String(tpr.tpr_eac)}</td>
                        </tr>
                        <tr>
                          <th style={{ width: "20%" }}>EAC TPR</th>
                          <td>{tpr.eac_tpr}</td>
                        </tr>
                        <tr>
                          <th style={{ width: "20%" }}>EAC EFD</th>
                          <td>{tpr.eac_efd}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Display the TAQ data for half-hourly meters */}
          {isHalfHourly && aqCalc && (
            <div className="mt-4">
              <div className="meter-details-header">
                <h2>
                  <BarChart className="me-2" />
                  Half-Hourly Meter Data
                </h2>
              </div>
              <div className="row g-3">
                <table
                  className="table table-bordered"
                  style={{ width: "90%", margin: "0 auto", marginTop: "15px" }}
                >
                  <tbody>
                    <tr>
                      <th style={{ width: "20%" }}>TAQ</th>
                      <td>{String(aqCalc.taq)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Display a message on the Ui if the meter is half hourly but the api failed to retrieve the TAQ calculation. */}
          {isHalfHourly && !aqCalc && (
            <div className="mt-4">
              <div className="meter-details-header">
                <h2>
                  <BarChart className="me-2" />
                  Half-Hourly Meter Data
                </h2>
              </div>
              <div className="alert alert-warning">
                This is a half-hourly meter, but TAQ data could not be retrieved. Please try again.
              </div>
            </div>
          )}
        </>
      ) : null}
      <div className="meter-details-footer">
        <Button onClick={displayMeterDetails}>
          {showMeterDetails ? "Hide Details" : "Show Meter Details"}
        </Button>
      </div>
    </div>
  );
};

export default MeterDetailsView;
